/**
 * Session creation hook for creating chat sessions
 * Follows ADR-003: High Cohesion - Single responsibility: create sessions
 */

import { useCallback } from 'react';
import { log, logWarn } from '../utils/logger';
import type { EventCreationParams } from 'parlant-client/src/api';

/**
 * Session data structure for creating sessions
 */
export interface SessionData {
  agent_id: string;
  title: string;
  customer_id?: string;
  metadata?: Record<string, unknown>;
  labels?: string[];
}

/**
 * Created session response
 */
export interface CreatedSession {
  id: string;
  customer_id?: string;
}

/**
 * Options for useSessionCreation hook
 */
export interface UseSessionCreationOptions {
  /** Server URL */
  serverUrl: string;
  /** Agent ID */
  agentId: string;
  /** Authentication token */
  authToken?: string;
}

/**
 * Return value for useSessionCreation hook
 */
export interface UseSessionCreationReturn {
  /** Creates a new session */
  createSession: (customerId?: string, initialMessage?: EventCreationParams) => Promise<CreatedSession>;
}

/**
 * Handles HTTP error responses and throws appropriate errors
 */
async function handleSessionError(response: Response, agentId: string): Promise<never> {
  let errorData;
  try {
    errorData = await response.json();
  } catch {
    errorData = { message: response.statusText };
  }

  if (response.status === 401) {
    throw new Error('Authentication failed: Invalid or expired token. Please log in again.');
  }
  if (response.status === 403) {
    throw new Error('Access denied: You do not have permission to create sessions.');
  }
  if (response.status === 404) {
    const errorDetail = typeof errorData === 'object' && errorData !== null && 'detail' in errorData
      ? String((errorData as { detail?: unknown }).detail)
      : '';
    
    if (errorDetail.includes('Item') && errorDetail.includes('not found')) {
      const idMatch = errorDetail.match(/Item '([^']+)' not found/);
      const itemId = idMatch ? idMatch[1] : agentId;
      throw new Error(`Agent not found. Please check that the agent ID '${itemId}' exists and is correct.`);
    }
    throw new Error(`Resource not found: ${errorDetail || JSON.stringify(errorData)}`);
  }
  if (response.status === 409) {
    throw new Error('Session conflict: A session with this ID already exists.');
  }
  if (response.status === 429) {
    throw new Error('Too many requests. Please wait a moment and try again.');
  }
  if (response.status === 500) {
    throw new Error('Internal server error. Please try again later.');
  }
  if (response.status === 502 || response.status === 503) {
    throw new Error('Service temporarily unavailable. Please try again later.');
  }
  if (response.status === 504) {
    throw new Error('Request timeout. The server took too long to respond. Please try again.');
  }
  if (response.status >= 400 && response.status < 500) {
    throw new Error(`Client error (${response.status}): ${JSON.stringify(errorData)}`);
  }
  if (response.status >= 500) {
    throw new Error(`Server error (${response.status}): Please try again later.`);
  }

  throw new Error(`HTTP ${response.status}: ${JSON.stringify(errorData)}`);
}

/**
 * Custom hook for creating chat sessions
 * 
 * @param options - Configuration options
 * @returns Session creation functions
 */
export function useSessionCreation({
  serverUrl,
  agentId,
  authToken
}: UseSessionCreationOptions): UseSessionCreationReturn {
  const cleanServerUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;

  /**
   * Sends an initial message to a session
   */
  const sendInitialMessage = useCallback(async (
    sessionId: string,
    message: EventCreationParams
  ): Promise<void> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${cleanServerUrl}/sessions/${sessionId}/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify(message)
    });

    if (!response.ok) {
      const errorData = await response.json();
      logWarn('Failed to send initial message:', errorData);
    }
  }, [cleanServerUrl, authToken]);

  /**
   * Creates a new session
   */
  const createSession = useCallback(async (
    customerId?: string,
    initialMessage?: EventCreationParams
  ): Promise<CreatedSession> => {
    const now = new Date();
    const sessionData: SessionData = {
      agent_id: agentId,
      title: `Chat Session ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`,
    };

    if (customerId) {
      sessionData.customer_id = customerId;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
      log('Using auth token for session creation');
    }

    log('Creating session with data:', sessionData);

    const response = await fetch(`${cleanServerUrl}/sessions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(sessionData)
    });

    if (!response.ok) {
      await handleSessionError(response, agentId);
    }

    const newSession = await response.json();
    log('Session created:', newSession.id);

    if (!newSession?.id) {
      throw new Error('Session was not created - no ID returned');
    }

    // Send initial message if provided
    if (initialMessage) {
      await sendInitialMessage(newSession.id, initialMessage);
    }

    return {
      id: newSession.id,
      customer_id: newSession.customer_id
    };
  }, [agentId, cleanServerUrl, authToken, sendInitialMessage]);

  return {
    createSession
  };
}

